import type { AuthSession, SignInCallback } from '@/global.d.ts';

import { baseUrls, cache as config } from '@/config.ts';
import { Logger } from '@/lib/log.ts';

import { invalidate as invalidateCache } from './cache.ts';
import * as events from './events.ts';

const log = Logger('header.authentication');

let AuthJS;

/**
 * load auth api
 */
export async function loadAPI() {
	if (AuthJS) {
		return;
	}

	try {
		// @ts-expect-error Uses Vite resolve.alias
		AuthJS = (await import('__AUTHJS_URL__')).api;
		log.debug('AuthJS API loaded');

		// expose global auth api for legacy applications, which are not using auth-esm.js yet (e.g. Forum, LiveContent)
		if (window.DERSTANDARD) {
			window.DERSTANDARD.auth = { api: AuthJS };
			// Because there might be race conditions, where a consumer application
			// starts faster than the header, the consumer can listen to this event to
			// ensure that the `window.DERSTANDARD.auth` object is available.
			//
			// This is just a patch until we implement a better way to handle
			// authentication in general. An example of how to use this event can be
			// found in the `forum-frontend`.
			window.dispatchEvent(new CustomEvent(events.dst.auth.initialized));
		}
	} catch (err) {
		throw new Error(`AuthJS API could not be loaded - ${err}`);
	}
}

const authorizationBaseUrl =
	window.DERSTANDARD?.pageConfig?.edition === 'de' ? baseUrls.authorization_de : baseUrls.authorization_at;

/**
 * Returns session token
 * @returns
 */
async function getSession(): Promise<AuthSession> {
	await loadAPI();

	return await AuthJS.getSession();
}

/**
 * Trigger logout for current user
 * @returns
 */
async function iamLogout(): Promise<void> {
	const response = await window.fetch(`${authorizationBaseUrl}/api/user`, {
		cache: 'default',
		credentials: 'include',
		headers: {
			'X-Requested-With': 'XMLHttpRequest',
		},
		method: 'POST',
	});

	if (response['ok']) {
		return Promise.resolve();
	}

	return Promise.reject(new Error(`HTTP ${response['status']}`));
}

/**
 *
 * @param callback
 */
async function onSignedIn(callback: SignInCallback): Promise<void> {
	await loadAPI();

	if (AuthJS) {
		AuthJS.onSignedIn = async (e: Event) => callback(e);
	}
}

/**
 *
 * @param callback
 */
async function onStateChanged(callback: SignInCallback): Promise<void> {
	await loadAPI();

	if (AuthJS) {
		AuthJS.onStateChanged = async (e: Event) => callback(e);
	}
}

/**
 * Sign in a user
 * @returns
 */
async function signIn(): Promise<boolean> {
	await loadAPI();

	return await AuthJS.signIn({
		overlayOptions: {
			noCloseButton: false,
		},
		triggerType: 'overlay',
	});
}

/**
 * Sign put a user
 * @returns
 */
async function signOut(): Promise<boolean> {
	window.dispatchEvent(new CustomEvent(events.dst.user.loggedOut));
	await iamLogout();
	invalidateCache(config.userDataKey);

	await loadAPI();

	return await AuthJS.signOut();
}

/**
 * Returns whetherthe current user is logged in
 * @returns
 */
async function userIsLoggedIn(): Promise<boolean> {
	await loadAPI();

	try {
		const session = await AuthJS.getSession();

		return session !== null;
	} catch (_error) {
		return false;
	}
}

export { getSession, iamLogout, onSignedIn, onStateChanged, signIn, signOut, userIsLoggedIn };
